import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './index.css';

import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Statistic,
  Table,
  Typography,
} from 'antd';
import {
  MailOutlined,
  ReloadOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { dashApi } from '../../api/dash';

const { Title, Text } = Typography;

/*
const legacyMockData = {
  topStats: [
    { title: 'Total Shipments', value: 1248, trend: 5.2, trendType: 'up', color: '#1f63fb' },
    { title: 'In Transit', value: 356, subtitle: 'Share of total 28.6%', color: '#722ed1' },
    { title: 'Arrived', value: 235, trend: 8.7, trendType: 'up', color: '#52c41a' },
    { title: 'Exception Shipments', value: 156, trend: 2.3, trendType: 'down', color: '#de1c1c' },
  ],
  transportStages: [
    { name: 'Booking Confirmed', date: '2024-06-01', status: 'completed', color: '#5ec26a' },
    { name: 'Container Loaded', date: '2024-06-05', status: 'completed', color: '#5ec26a' },
    { name: 'Loaded On Board', date: '2024-06-10', status: 'completed', color: '#5ec26a' },
    { name: 'In Transit', date: '2024-06-15', status: 'completed', color: '#5ec26a' },
    { name: 'Arrived', date: '2024-06-28', status: 'current', color: '#ff4d4f' },
    { name: 'Customs Exam', date: 'ETA: 2024-07-02', status: 'pending', color: '#d9d9d9' },
    { name: 'Customs Released', date: 'ETA: 2024-07-03', status: 'pending', color: '#d9d9d9' },
    { name: 'Carrier Released', date: 'ETA: 2024-07-04', status: 'pending', color: '#d9d9d9' },
    { name: 'Terminal Discharge', date: 'ETA: 2024-07-05', status: 'pending', color: '#d9d9d9' },
    { name: 'Completed', date: 'ETA: 2024-07-10', status: 'pending', color: '#d9d9d9' },
  ],
  processingStages: [
    { name: 'Booking Confirmed', percent: 0, color: '#e2e8f0' },
    { name: 'Pre-allocation', percent: 0, color: '#e2e8f0' },
    { name: 'Container Loaded', percent: 25, color: '#dcfce7' },
    { name: 'Stowage', percent: 40, color: '#fffbeb' },
    { name: 'B/L Confirmed', percent: 45, color: '#fffbeb' },
    { name: 'Customs', percent: 30, color: '#fffbeb' },
    { name: 'Released', percent: 25, color: '#dcfce7' },
    { name: 'On Board', percent: 30, color: '#fffbeb' },
    { name: 'In Transit', percent: 28, color: '#dcfce7', alert: true },
    { name: 'Pickup Notice', percent: 25, color: '#dcfce7' },
    { name: 'Arrival', percent: 23, color: '#dcfce7' },
    { name: 'Pickup', percent: 22, color: '#dcfce7', alert: true },
    { name: 'Completed', percent: 20, color: '#dcfce7' },
    { name: 'Delivery', percent: 19, color: '#dcfce7' },
  ],
  bottomMetrics: [
    { name: 'Pending Customs Release', value: 142, total: 500, color: '#ff7a45' },
    { name: 'Pending Carrier Release', value: 128, total: 500, color: '#13c2c2' },
    { name: 'Pending Customer Pickup', value: 134, total: 500, color: '#52c41a' },
  ],
  exceptionStatus: [
    { name: 'Customs Exam', value: 48, description: 'Incomplete docs and classification issues', avgResolveTime: '3-5 days', icon: 'CE', color: '#fa8c16' },
    { name: 'Delay', value: 36, description: 'Weather and port congestion', avgResolveTime: '2-7 days', icon: 'DL', color: '#faad14' },
    { name: 'Document Issues', value: 72, description: 'B/L errors and missing C/O', avgResolveTime: '1-3 days', icon: 'DOC', color: '#1f63fb' },
  ],
  recentOperations: [
    { type: 'New', title: 'New shipment #1001', description: 'Created by Zhang San MBL: MBL20240001', time: '2024-06-28 09:15', icon: 'new' },
    { type: 'Update', title: 'Shipment status updated', description: 'Li Si changed shipment #1002 from In Transit to Arrived', time: '2024-06-28 08:40', icon: 'update' },
    { type: 'Flag', title: 'Marked exception', description: 'Wang Wu marked shipment #1003 as Customs Exam exception', time: '2024-06-28 07:22', icon: 'flag' },
  ],
};
*/

const numberRenderer = (value) => Number(value || 0).toLocaleString();

function Dash() {
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState('');

  const fetchEmailSummary = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await dashApi.getEmailSummary();
      const responseBody = response?.data || {};

      if (responseBody.code !== 200) {
        throw new Error(responseBody.message || 'Failed to load email summary');
      }

      const list = Array.isArray(responseBody.data) ? responseBody.data : [];
      setSummaryData(list);
      setLastUpdatedAt(new Date().toLocaleString());
    } catch (error) {
      console.error('fetch email summary failed:', error);
      setSummaryData([]);
      setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to load email summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmailSummary();
  }, [fetchEmailSummary]);

  const totals = useMemo(() => {
    return summaryData.reduce((acc, item) => {
      acc.yesterdayPullCount += Number(item.yesterdayPullCount || 0);
      acc.yesterdayForwardCount += Number(item.yesterdayForwardCount || 0);
      acc.todayPullCount += Number(item.todayPullCount || 0);
      acc.todayForwardCount += Number(item.todayForwardCount || 0);
      return acc;
    }, {
      yesterdayPullCount: 0,
      yesterdayForwardCount: 0,
      todayPullCount: 0,
      todayForwardCount: 0,
    });
  }, [summaryData]);

  const columns = useMemo(() => [
    {
      title: 'Email Account',
      dataIndex: 'account',
      key: 'account',
      fixed: 'left',
      minWidth: 260,
      render: (value) => value || '-',
    },
    {
      title: 'Yesterday Pull',
      dataIndex: 'yesterdayPullCount',
      key: 'yesterdayPullCount',
      align: 'right',
      width: 150,
      render: numberRenderer,
    },
    {
      title: 'Yesterday Forward',
      dataIndex: 'yesterdayForwardCount',
      key: 'yesterdayForwardCount',
      align: 'right',
      width: 150,
      render: numberRenderer,
    },
    {
      title: 'Today Pull',
      dataIndex: 'todayPullCount',
      key: 'todayPullCount',
      align: 'right',
      width: 150,
      sorter: (a, b) => Number(a.todayPullCount || 0) - Number(b.todayPullCount || 0),
      defaultSortOrder: 'descend',
      render: numberRenderer,
    },
    {
      title: 'Today Forward',
      dataIndex: 'todayForwardCount',
      key: 'todayForwardCount',
      align: 'right',
      width: 150,
      render: numberRenderer,
    },
  ], []);

  return (
    <div className="dash email-summary-dash">
      <div className="dash-header">
        <div>
          <Title level={3} style={{ margin: 0 }}>Email Summary</Title>
          <Text className="update-time">
            {lastUpdatedAt ? `Updated at ${lastUpdatedAt}` : 'Email pull and valid forward summary by account'}
          </Text>
        </div>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={fetchEmailSummary}
        >
          Refresh
        </Button>
      </div>

      <div className="dash-content">
        {errorMessage ? (
          <Alert
            type="error"
            showIcon
            message="Failed to load email summary"
            description={errorMessage}
          />
        ) : null}

        <Row gutter={[16, 16]} className="email-summary-stats">
          <Col xs={24} sm={12} lg={6}>
            <Card className="email-stat-card email-stat-card-primary">
              <Statistic
                title="Today Pull"
                value={totals.todayPullCount}
                prefix={<MailOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="email-stat-card email-stat-card-cyan">
              <Statistic
                title="Today Forward"
                value={totals.todayForwardCount}
                prefix={<SendOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="email-stat-card email-stat-card-indigo">
              <Statistic
                title="Yesterday Pull"
                value={totals.yesterdayPullCount}
                prefix={<MailOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="email-stat-card email-stat-card-green">
              <Statistic
                title="Yesterday Forward"
                value={totals.yesterdayForwardCount}
                prefix={<SendOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Card
          className="email-summary-table-card"
          title="Email Details"
          extra={<Text type="secondary">{summaryData.length} Accounts</Text>}
        >
          <Table
            rowKey={(record) => record.account}
            loading={loading}
            columns={columns}
            dataSource={summaryData}
            pagination={false}
            scroll={{ x: 860 }}
            size='small'
          />
        </Card>
      </div>
    </div>
  );
}

export default Dash;
